import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  verifyAuthOTP,
  generateOTP,
} from "../../redux/auth";
import CodeInput from "../../components/formInputs/CodeInput";
import Button from "../../components/buttons/Button";
import MangoLogo from "../../components/icons/MangoLogo";
import "../../components/styles/otpVerificationPage.scss";

/**
 * OTPVerificationPage — 6-digit OTP entry screen
 * Unified passwordless flow: verify OTP → auto sign-up/sign-in → /home
 */
const OTPVerificationPage = () => {
  const navigate = useNavigate();
  const { isLoading, error, email } = useSelector((state) => state.auth);

  const [otpCode, setOtpCode] = useState("");
  const [resendTimer, setResendTimer] = useState(60);

  // Masked email display
  const destination = email
    ? `${email.slice(0, 3)}***@${email.split("@")[1] || ""}`
    : "";

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleVerify = useCallback(async () => {
    if (otpCode.length < 6 || isLoading) return;

    const success = await verifyAuthOTP(otpCode);

    if (success) {
      navigate("/home");
    }
  }, [otpCode, isLoading, navigate]);

  const handleResend = useCallback(async () => {
    if (resendTimer > 0) return;
    await generateOTP(email);
    setResendTimer(60);
    setOtpCode("");
  }, [resendTimer, email]);

  const title = "Verify Login";

  return (
    <div className="otp-page">
      {/* LEFT: Onboarding Sidebar */}
      <div className="otp-page__image-container">
        <img
          src="/assets/onboarding-sidebar-bg.jpg"
          alt=""
          className="otp-page__sidebar-bg"
        />
      </div>

      {/* RIGHT: Form Container */}
      <div className="otp-page__container">
        <div className="otp-page__bg-pattern">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <circle cx="215" cy="493" r="2" fill="#404040" opacity="0.5" />
            <circle cx="328" cy="129" r="2" fill="#404040" opacity="0.5" />
            <circle cx="492" cy="975" r="2" fill="#404040" opacity="0.5" />
            <circle cx="772" cy="207" r="2" fill="#404040" opacity="0.5" />
            <circle cx="593" cy="66" r="2" fill="#404040" opacity="0.5" />
          </svg>
        </div>

        <div className="otp-page__wrapper">
          <div className="otp-page__content">
            {/* Title Section */}
            <div className="otp-page__title-section">
              <MangoLogo size={80} />
              <h1 className="otp-page__title">{title}</h1>
              <p className="otp-page__subtitle">
                We've sent a 6-digit verification code to{" "}
                <strong>{destination}</strong>
              </p>
            </div>

            {/* OTP Code Input */}
            <div className="otp-page__form-group">
              <CodeInput
                length={6}
                value={otpCode}
                onChange={setOtpCode}
                error={!!error}
              />

              {/* Error */}
              {error && (
                <p className="otp-page__error">{error}</p>
              )}

              {/* Resend */}
              <div className="otp-page__resend">
                {resendTimer > 0 ? (
                  <p className="otp-page__resend-timer">
                    Resend code in <span>{resendTimer}s</span>
                  </p>
                ) : (
                  <button
                    className="otp-page__resend-btn"
                    onClick={handleResend}
                  >
                    Resend Code
                  </button>
                )}
              </div>

              {/* Verify Button */}
              <Button
                label="Verify"
                size="lg"
                variant="primary"
                isDisabled={otpCode.length < 6}
                isLoading={isLoading}
                action={handleVerify}
              />
            </div>
          </div>

          <p className="otp-page__bottom-link">
            Wrong account?{" "}
            <span onClick={() => navigate("/")}>Back to Login</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationPage;
